import { reviewingEndpoints } from '@/core/endpoints';
import { SuggestionsCategory } from '@/models/chat.models';
import { Conversation } from '@/models/conversation';
import { KeyPointResponse, ReviewMessage } from '@/models/doc-review';
import { AxiosError } from 'axios';
import httpService from './HttpService';

class DocumentReviewingService {
  /**
   * Sends paragraphs to the service to get improvement suggestions.
   * @param {string[]} paragraphs - An array of strings representing the document's paragraphs.
   * @returns {Promise<SuggestionsCategory[]>} - A promise that resolves with the generated suggestions.
   * @throws {string | Error} - Throws an error if the request fails.
   */
  async getDocumentSuggestions(
    paragraphs: string[]
  ): Promise<SuggestionsCategory[]> {
    try {
      const response = await httpService.post<{
        suggestions: SuggestionsCategory[];
      }>(reviewingEndpoints.DOCUMENT_SUGGESTIONS, {
        documentParagraphs: paragraphs
      });
      return response.suggestions;
    } catch (error) {
      if (
        (error as AxiosError).response?.status === 400 &&
        (error as AxiosError).response?.data
      ) {
        throw ((error as AxiosError).response!.data as Array<string>)[0];
      }
      throw error;
    }
  }

  /**
   * Sends a question related to the current chat context and updates paragraphs.
   * @param {string} question - The question text to ask.
   * @param {string[]} paragraphs - The updated document paragraphs.
   * @returns {Promise<{ message: ReviewMessage; pickle: string }>} - A promise resolving to the received ReviewMessage.
   */
  async askQuestion(
    question: string,
    paragraphs: string[],
    conversation: Conversation
  ): Promise<{ responseMessage: ReviewMessage; pickle: string }> {
    try {
      const response = await httpService.post<{
        responseMessage: ReviewMessage;
        pickle: string;
      }>(reviewingEndpoints.REVIEWING_CHAT, {
        messages: conversation.messages,
        documentParagraphs: paragraphs,
        pickle: conversation.pickle,
        question
      });

      conversation.pickle = response.pickle;
      return response;
    } catch (error) {
      console.error('Error in DocumentReviewingService.askQuestion:', error);
      throw error;
    }
  }

  /**
   * Sends paragraphs to the service to extract key points.
   * @param {string[]} paragraphs - An array of strings representing the document's paragraphs.
   * @returns {Promise<KeyPointResponse>} - A promise that resolves with the extracted key points.
   * @throws {string | Error} - Throws an error if the request fails.
   */
  async getDocumentKeypoints(paragraphs: string[]): Promise<KeyPointResponse> {
    try {
      const response = await httpService.post<KeyPointResponse>(
        reviewingEndpoints.DOCUMENT_KEYPOINTS,
        {
          documentParagraphs: paragraphs
        }
      );
      return response;
    } catch (error) {
      if (
        (error as AxiosError).response?.status === 400 &&
        (error as AxiosError).response?.data
      ) {
        throw ((error as AxiosError).response!.data as Array<string>)[0];
      }
      throw error;
    }
  }
}

const documentReviewingService = new DocumentReviewingService();
export default documentReviewingService;
